import { BaseResponseSchema, getRequiredMessage } from '@monetize/types/common';
import { isValidDate } from '@monetize/utils/core';
import { AxiosError } from 'axios';
import { parseISO } from 'date-fns/parseISO';
import isDate from 'lodash/isDate';
import { UseFormReturn } from 'react-hook-form';
import { boolean, string, z } from 'zod';
import { AddressSchema } from './address.types';
import { ContactRespSchema } from './contact.types';
import { SigningOrderEnumZ, StorageTypeEnumZ } from './enums.types';
import { LegalEntitySnapshot } from './legalEntity.types';
import { OpportunitySummaryRespSchema } from './opportunity.types';
import { QuoteBasicRespSchema } from './quote.types';
import {
  ReferenceResourceCategoryEnum,
  ReferenceResourceTypeEnum,
} from './referenceResource.types';
import { StorageSchema } from './storage.types';

const validateUrlOrEmpty = (val?: string | null) => {
  if (!val) {
    return true;
  }

  try {
    new URL(val);
    return true;
  } catch {
    return false;
  }
};
export const DealRoomSettingSchema = z.object({
  enabled: z.boolean(),
  useDefaultWelcomeMessage: z.boolean(),
  defaultWelcomeMessage: z.string().nullish(),
  defaultWelcomeUrl: z.url().nullish(),
  canEndCustomerChangeSigningContact: z.object({
    canChange: z.boolean(),
    canCreate: z.boolean(),
  }),
});

export type IDealRoomSettingSchema = z.infer<typeof DealRoomSettingSchema>;

export const DealRoomSettingWelcomeSchema = z.object({
  useDefaultWelcomeMessage: z.boolean(),
  defaultWelcomeMessage: z.string().nullish(),
  defaultWelcomeUrl: z.url().or(z.literal('')).nullable().optional(),
  defaultWelcomeUrlLabel: z.string().nullish(),
});

export type IDealRoomSettingWelcomeSchema = z.infer<
  typeof DealRoomSettingWelcomeSchema
>;

export enum DealRoomStatusEnum {
  DRAFT = 'DRAFT',
  SHARED = 'SHARED',
  IN_REVIEW = 'IN_REVIEW',
  DECLINED = 'DECLINED',
  SIGNED = 'SIGNED',
  EXPIRED = 'EXPIRED',
  CANCELED = 'CANCELED',
}
export const DealRoomStatusEnumSchemaZ = z.enum(DealRoomStatusEnum);

export const DealRoomStatusDisplayText: {
  [key in DealRoomStatusEnum]: string;
} = {
  DRAFT: 'Draft',
  SHARED: 'Published',
  IN_REVIEW: 'In-Review',
  DECLINED: 'Declined',
  SIGNED: 'Signed',
  EXPIRED: 'Expired',
  CANCELED: 'Canceled',
};

export enum DealRoomDocumentSectionKeyEnum {
  WELCOME = 'WELCOME',
  FEATURES = 'FEATURES',
  EXTRA_INFO = 'EXTRA_INFO',
}
export const DealRoomDocumentSectionKeyEnumZ = z.enum(
  DealRoomDocumentSectionKeyEnum,
);

export const DEAL_ROOM_WELCOME_MESSAGE_CONTENT_KEY = 'WELCOME_MESSAGE';

export const DealRoomTextContentSchema = z.object({
  key: z.string(),
  contentType: z.literal('TEXT'),
  name: z.string().nullish(),
  description: z.string().nullish(),
  content: z.string(),
});
export type DealRoomTextContent = z.infer<typeof DealRoomTextContentSchema>;
export const DealRoomUrlContentSchema = z.object({
  key: z.string(),
  contentType: z.literal('URL'),
  name: z.string().nullish(),
  description: z.string().nullish(),
  content: z.object({
    link: z.string().nullish().refine(validateUrlOrEmpty, 'Invalid URL format'),
    label: z.string().nullish(),
  }),
});

export type DealRoomUrlContent = z.infer<typeof DealRoomUrlContentSchema>;

export const StorageObjectDetailsSchema = StorageSchema.omit({
  contentBase64Encoded: true,
});
export type StorageObjectDetails = z.infer<typeof StorageObjectDetailsSchema>;

export const DealRoomReferenceResourceContentSchema = z.object({
  key: z.string(),
  contentType: z.literal('REFERENCE_RESOURCE'),
  content: z.object({
    id: z.string(),
    name: z.string(),
    description: z.string().nullish(),
    resourceType: z.enum(ReferenceResourceTypeEnum),
    category: z.enum(ReferenceResourceCategoryEnum),
    resourceDetails: z.union([
      z.object({
        storageObjectId: z.string(),
        storageObjectDetails: StorageObjectDetailsSchema.nullish(),
        contentType: z.literal('FILE'),
      }),
      z.object({
        url: z.object({
          link: z.url(),
          label: z.string().nullish(),
        }),
        contentType: z.literal('URL'),
      }),
    ]),
  }),
});
export type DealRoomReferenceResourceContent = z.infer<
  typeof DealRoomReferenceResourceContentSchema
>;

export const DealRoomRichTextContentSchema = z.object({
  key: z.string(),
  contentType: z.literal('RICH_TEXT'),
  name: z.string().nullish(),
  description: z.string().nullish(),
  content: z.string(),
});
export type DealRoomRichTextContent = z.infer<
  typeof DealRoomRichTextContentSchema
>;

export const DealRoomAttachmentContentSchema = z.object({
  key: z.string(),
  contentType: z.literal('ATTACHMENT'),
  name: z.string().nullish(),
  description: z.string().nullish(),
  content: z.object({
    storageObjectDetails: StorageObjectDetailsSchema.nullish(),
    storageObjectId: z.string(),
  }),
});
export type DealRoomAttachmentContent = z.infer<
  typeof DealRoomAttachmentContentSchema
>;

export const DealRoomDocumentSectionContentSchema = z.union([
  DealRoomTextContentSchema,
  DealRoomUrlContentSchema,
  DealRoomReferenceResourceContentSchema,
  DealRoomAttachmentContentSchema,
  DealRoomRichTextContentSchema,
]);

export const DealRoomDocumentSectionSchema = z.object({
  key: DealRoomDocumentSectionKeyEnumZ,
  title: z.string().optional(),
  description: z.string().nullish(),
  contents: z.array(DealRoomDocumentSectionContentSchema),
});
export type DealRoomDocumentSectionContent = z.infer<
  typeof DealRoomDocumentSectionContentSchema
>;

export type DealRoomDocumentSection = z.infer<
  typeof DealRoomDocumentSectionSchema
>;
export const DealRoomDocumentSchema = z.object({
  sections: z.array(DealRoomDocumentSectionSchema),
});
export const DealRoomQuoteRespSchema = z.object({
  quote: QuoteBasicRespSchema,
  name: z.string(),
  note: z.string().nullish(),
  position: z.number(),
});
export type DealRoomQuoteResp = z.infer<typeof DealRoomQuoteRespSchema>;

export const DealRoomContactRespSchema = z.object({
  snapshotId: string().nullish(),
  address: AddressSchema.nullish(),
  customId: string().nullable(),
  email: string(),
  fullName: string(),
  internal: boolean().optional(),
  phone: string().nullable(),
  title: string().nullable(),
  id: string(),
});
export type IDealRoomContactRespSchema = z.infer<
  typeof DealRoomContactRespSchema
>;

export const DealRoomContactsRespSchema = z.object({
  additionalESignRecipients: z.array(DealRoomContactRespSchema),
  esign: z.array(ContactRespSchema.extend({ internal: z.boolean() })),
  billingContact: ContactRespSchema.nullish(),
  shippingContact: ContactRespSchema.nullish(),
});
export type DealRoomContactsResp = z.infer<typeof DealRoomContactsRespSchema>;

export const DealRoomContractDocumentSchema = z.object({
  storageObjectId: z.string().nullish(),
  documentUrl: z.string().nullish(),
  storageType: StorageTypeEnumZ,
  contentType: z.union([z.literal('URL'), z.literal('FILE')]),
});
export type DealRoomContractDocument = z.infer<
  typeof DealRoomContractDocumentSchema
>;

export const DealRoomRespSchema = BaseResponseSchema.extend({
  id: z.string(),
  customId: z.string().optional(),
  expirationDate: z.string(),
  status: DealRoomStatusEnumSchemaZ,
  opportunity: OpportunitySummaryRespSchema,
  quotes: z.array(DealRoomQuoteRespSchema),
  fromCompany: LegalEntitySnapshot,
  companyLogo: z.string().nullish(),
  owner: z.object({
    id: z.string(),
    name: z.string(),
    email: z.string(),
  }),
  purchaseOrderNumber: z.string().max(255).nullish(),
  requiresEsign: z.boolean(),
  selectedQuoteId: z.string().optional(),
  accountId: z.string(),
  accountName: z.string(),
  contacts: DealRoomContactsRespSchema,
  shippingAddress: AddressSchema.nullish(),
  billingAddress: AddressSchema.nullish(),
  signingOrder: SigningOrderEnumZ,
  document: z.object({
    version: z.string(),
    document: DealRoomDocumentSchema,
  }),
  canEndCustomerChangeSigningContact: z
    .object({
      canChange: z.boolean(),
      canCreate: z.boolean(),
    })
    .nullish(),
  contractDocuments: z.array(DealRoomContractDocumentSchema),
});
export type DealRoomResp = z.infer<typeof DealRoomRespSchema>;

export const DealRoomQuoteItemReqSchema = z.object({
  quoteId: z.string(),
  customName: z.string(),
  note: z.string().nullish(),
  position: z.number(),
});
export type DealRoomQuoteItemReq = z.infer<typeof DealRoomQuoteItemReqSchema>;

export const DealRoomContactsReqSchema = z.object({
  shippingId: z.string().nullish(),
  billingId: z.string().nullish(),
  esignCcIds: z.array(z.string()),
  esignIds: z.array(z.string()),
});
export type DealRoomContactsReq = z.infer<typeof DealRoomContactsReqSchema>;

export const DealRoomReqSchema = z.object({
  customId: z.string().nullish(),
  expirationDate: z.string(),
  quotes: z.array(DealRoomQuoteItemReqSchema),
  purchaseOrderNumber: z.string().max(255).nullish(),
  requiresEsign: z.boolean(),
  selectedQuoteId: z.string().nullish(),
  document: z.object({
    version: z.string(),
    document: DealRoomDocumentSchema,
  }),
  signingOrder: SigningOrderEnumZ,
});
export type DealRoomReq = z.infer<typeof DealRoomReqSchema>;

export const DealRoomSharedReqSchema = z.object({
  purchaseOrderNumber: z.string().max(255).nullish(),
  selectedQuoteId: z.string().nullish(),
});
export type DealRoomSharedReq = z.infer<typeof DealRoomSharedReqSchema>;

export const DealRoomWelcomeSchema = z.object({
  title: z.string().nonempty(getRequiredMessage('Title')),
  message: z.string().nullish(),
});
export type DealRoomWelcome = z.infer<typeof DealRoomWelcomeSchema>;

export type DealRoomQuoteSelectRow = {
  id: string;
  position: number | null;
  selected: boolean;
};

export const DealRoomNextStepEsignContactSchema = z
  .object({
    requiresEsign: z.boolean(),
    signingOrder: SigningOrderEnumZ,

    internalEsignContactId: z.string().nullish(),
    externalEsignContactId: z.string().nullish(),
    esignCcContacts: z.array(DealRoomContactRespSchema),
  })
  .refine((data) => !data.requiresEsign || data.internalEsignContactId, {
    path: ['internalEsignContactId'],
    message: getRequiredMessage('Internal eSigner'),
  });

export type DealRoomNextStepEsignContact = z.infer<
  typeof DealRoomNextStepEsignContactSchema
>;

export const DealRoomNextStepContactSchema = z.object({
  shippingId: z.string().nonempty(getRequiredMessage('Shipping Contact')),
  billingId: z.string().nonempty(getRequiredMessage('Billing Contact')),
  useSameShippingBilling: z.boolean(),
});
export type DealRoomNextStepContact = z.infer<
  typeof DealRoomNextStepContactSchema
>;

export const DealRoomShareRequestSchema = z.object({
  expiresAt: z.union([z.string(), z.date()]).refine((val) => {
    const date = isDate(val) ? val : parseISO(val);
    return isValidDate(date);
  }, 'Invalid date'),
});
export type DealRoomShareRequest = z.infer<typeof DealRoomShareRequestSchema>;

export const DealRoomShareAccessResponseSchema = z.object({
  id: z.string(),
  expiresAt: z.string(),
  url: z.url(),
});
export type DealRoomShareAccessResponse = z.infer<
  typeof DealRoomShareAccessResponseSchema
>;

export type transitionDealRoomStatusTypeFn = ({
  newState,
  extraData,
}:
  | {
      newState: DealRoomStatusEnum.SHARED;
      extraData?: void;
    }
  | {
      newState: DealRoomStatusEnum.DRAFT;
      extraData?: void;
    }
  | {
      newState: DealRoomStatusEnum.DECLINED;
      extraData?: void;
    }
  | {
      newState: DealRoomStatusEnum.SIGNED;
      extraData?: void;
    }) => Promise<DealRoomResp | null>;

export interface DRSharedProps {
  methods: UseFormReturn<DealRoomReq, object>;
  handleSubmitButton: () => Promise<void>;
  handleSubmitButtonWithoutDirtyCheck: () => void;
  dealRoomResp: DealRoomResp;
  setDealRoomResp: (data: DealRoomResp) => void;
  isDRInitialLoading: boolean;
  isDRSaving: boolean;
  isDRSavingErrored: boolean;
  isDealRoomReadOnly: boolean;
  /** Directly update DealRoom without form */
  onSubmitDealRoom: (data: DealRoomReq) => Promise<DealRoomResp | null>;
  transitionDealRoomStatus: transitionDealRoomStatusTypeFn;
  isInternal: boolean;
  token?: string;
  tenantId?: string;
  isTenantEsignConfigured?: boolean;
  isExternalPreview?: boolean;
  setIsExternalPreview?: (isExternalPreview: boolean) => void;
}

export type UseDealRoomResponse = {
  dealRoomResp: DealRoomResp | null;
  errorDealRoom?: AxiosError | null;
  handleUpdateDealRoom: (data: DealRoomReq) => Promise<DealRoomResp>;
  isDRSaving: boolean;
  isDRSavingErrored: boolean;
  isInitialLoading: boolean;
  setDealRoomResp: (data: DealRoomResp) => void;
  transitionDealRoomStatus: transitionDealRoomStatusTypeFn;
};

export const SharedDealRoomContactSchema = z.object({
  id: string().optional(),
  fullName: string().min(1, getRequiredMessage('Name')),
  email: string().email(),
  phone: string().nullable(),
  title: string().nullable(),
  address: AddressSchema.nullish(),
});
export type SharedDealRoomContact = z.infer<typeof SharedDealRoomContactSchema>;

export const SharedDealRoomContactsReqSchema = z.object({
  billingContact: SharedDealRoomContactSchema.nullish(),
  shippingContact: SharedDealRoomContactSchema.nullish(),
  esignExternalContact: SharedDealRoomContactSchema.nullish(),
});
export type SharedDealRoomContactsReq = z.infer<
  typeof SharedDealRoomContactsReqSchema
>;

export const SharedDealRoomNextStepContactSchema = z.object({
  billingContact: SharedDealRoomContactSchema.nullish(),
  shippingContact: SharedDealRoomContactSchema.nullish(),
  useSameShippingBilling: z.boolean(),
});
export type SharedDealRoomNextStepContact = z.infer<
  typeof SharedDealRoomNextStepContactSchema
>;

export const SharedDealRoomSignSchema = z.object({
  esignExternalContact: SharedDealRoomContactSchema.nullish(),
});
export type SharedDealRoomSign = z.infer<typeof SharedDealRoomSignSchema>;

// TODO: add in esign contact
export const DealRoomExternalRequestSchema = z.object({
  // dealRoom: DealRoomReqSchema,
  selectedQuoteId: z.string().nullish(),
  purchaseOrderNumber: z.string().max(255).nullish(),
  requiresEsign: z.boolean(),
  contacts: z.object({
    billingContact: SharedDealRoomContactSchema.nullish(),
    shippingContact: SharedDealRoomContactSchema.nullish(),
    esignExternalContact: SharedDealRoomContactSchema.nullish(),
    useSameShippingBilling: z.boolean(),
  }),
});

export type DealRoomExternalRequest = z.infer<
  typeof DealRoomExternalRequestSchema
>;
